/* eslint-disable max-lines */
import type { Styles } from '@adornis/ass/style.js';
import { A } from '@adornis/base/env-info.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { downloadLink } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/fonts/fonts.js';
import '@adornis/forms/x-checkbox.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-dropdown-selection.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { changePassword, impersonateUser } from '@adornis/users/db/a-user.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import '@ils/locations/client/ils-location-multi-selection.js';
import '@ils/osces/client/ils-osce-type.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { prettyOsceCategory, prettyUserRole, UserRole } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { ILSUserOverviewLine } from '@legacy-db/ils-user-overview-line.js';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { map } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { getUserOverviewLine, subscribeSearchUser } from '../../api/user.js';
import type { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { OnlineStatus } from '../../db/online-status.js';
import '../components/ils-admin-dropdown-user-osce.js';
import '../components/ils-admin-order-detail.js';
import '../components/ils-examday-selector.js';
import { ILSUserEdit } from '../components/ils-user-edit.js';
import { ILSUserView } from '../components/ils-user-view.js';
import { onlineStatusDot } from '../components/online-status-dot.js';
import { allowedRoleKeys } from '../global-state.js';

@customElement('ils-users')
export class ILSUsers extends ChemistryLitElement {
  override styles(): Styles[] {
    return [{ ':host': { flex: '1', display: 'flex', flexDirection: 'column' } }];
  }

  @state() active = new RXController(this, UserRole.TRAINEE);

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());

  private readonly _filter = new AdornisFilter({
    search: '',
    aspects: [new StringAspect({ fieldName: 'user.roles.name', mode: 'equals', value: UserRole.TRAINEE })],
  });

  @state() private readonly _onlineUsers = new RXController(this, this._currentUser.observable, user$ =>
    user$.pipe(
      switchMap(user => {
        if (!user) return [];
        if (user.roles.find(role => role.name === UserRole.ADMIN)) return OnlineStatus.subscribeOnlineUsers()();
        return [];
      }),
    ),
  );

  @state() private readonly _view = new RXController(this, this._filter.whenChanging, filter$ =>
    filter$.pipe(map(filter => new View({ filter }))),
  );
  private get selectedRole() {
    return this._filter.aspects.find(fc => fc.fieldName === 'user.roles.name') as StringAspect | undefined;
  }

  override render() {
    return html`
      <x-flex flex ${css({ minHeight: '0' })}>
        <x-flex ${css({ borderBottom: `1px solid ${this.colors.neutral.shade(300)}` })}>
          <x-tabs .selected=${Object.values(UserRole).findIndex(r => r === this.active.value)}>
            ${repeat(
              allowedRoleKeys,
              r => r,
              role =>
                html`
                  <x-tab
                    @click="${() => {
                      this.active.next(UserRole[role]);
                      this._filter.setFilterAspect(
                        new StringAspect({ fieldName: 'user.roles.name', mode: 'equals', value: UserRole[role] }),
                      );
                    }}"
                  >
                    ${prettyUserRole(UserRole[role])}
                  </x-tab>
                `,
            )}
          </x-tabs>
          <x-flex horizontal wrap space="md" padding="xs">
            <x-input
              flex
              clearable
              placeholder="Suchen..."
              placeholder-mode="hide"
              @value-changed=${(e: ValueEvent<string>) => {
                if (this._filter.search !== e.detail.value) this._filter.search = e.detail.value;
              }}
            ></x-input>
            <ils-examday-selector
              single-line
              clearable
              @value-picked=${this._onExamDayValuePicked}
            ></ils-examday-selector>
            <x-button icon="add" @click=${() => ILSUserEdit.showPopup({ props: { userRole: this.active.value } })}>
              Nutzer hinzufügen
            </x-button>
            <x-button mode="outline" icon="download" @click=${() => this._downloadSignups()}> Anmeldungen </x-button>
          </x-flex>
        </x-flex>
        <x-flex flex> ${this.dataview()} </x-flex>
      </x-flex>
    `;
  }

  private readonly _onExamDayValuePicked = (e: ValueEvent<ExamDay | null>) => {
    if (e.detail.value) {
      return this._filter.setFilterAspect(
        new StringAspect({ mode: 'contains', fieldName: 'examDayID', value: e.detail.value._id }),
      );
    }

    const filterIndex = this._filter.aspects.findIndex(aspect => aspect.fieldName === 'examDayID');
    if (filterIndex >= 0) this._filter.aspects.splice(filterIndex, 1);
  };

  private dataview() {
    if (!this._view.value) return html` <x-loading-state></x-loading-state> `;
    return html`
      <x-data-view
        .options=${{
          class: ILSUserOverviewLine,
          operation: subscribeSearchUser,
          isSortable: true,
          isColumnResizable: false,
          isColumnResortable: false,
          isEntryClickable: false,
          loader: () =>
            html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center> <ils-loader></ils-loader> </x-flex>`,
          emptyState: () =>
            html`
              <x-flex
                ${css({
                  height: '200px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gridColumn: '1 / -1',
                })}
              >
                <x-h3 center> 🦗 Nichts anzuzeigen </x-h3>
              </x-flex>
            `,
          view: this._view.value,
          customColumns: this._dataviewColumns(),
        } satisfies DataViewOptions<typeof ILSUserOverviewLine>}
      ></x-data-view>
    `;
  }

  private _dataviewColumns(): Array<ColumnDefinition<typeof ILSUserOverviewLine>> {
    return [this._nameColumn(), this._ordersColumn(), this._userSpecificColumn(), this._actionsColumn()].filter(
      <T>(x: T): x is NonNullable<T> => x !== null,
    );
  }

  private _userSpecificColumn(): ColumnDefinition<typeof ILSUserOverviewLine> {
    return {
      customID: '_userRoleSpecificField',
      selectionSet: {
        user: {
          assignedOSCETypes: 1,
          ecommerce: { firstName: 1, lastName: 1 },
          specialization: 1,
          roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
        },
        candidateOfExamDays: {
          examDayID: 1,
          name: 1,
        },
      },
      renderItem: (line: ILSUserOverviewLine) => {
        if (!hasPermission(this._currentUser.value, 'users', 'modify')) return html` <div></div> `;
        switch (this.selectedRole?.value) {
          case UserRole.TRAINEE: {
            return html`
              <x-flex center>
                <ils-admin-dropdown-user-osce-trigger .user=${line} .examDays=${line.candidateOfExamDays}>
                </ils-admin-dropdown-user-osce-trigger>
              </x-flex>
            `;
          }
          case UserRole.TESTER: {
            const selected = line.user.assignedOSCETypes;
            return html`
              <x-flex center>
                <x-text>
                  ${selected?.length
                    ? selected.length === 1
                      ? prettyOsceCategory(selected[0])
                      : `${prettyOsceCategory(selected[0])} + ${selected.length - 1}`
                    : ''}
                </x-text>
              </x-flex>
            `;
          }
          case UserRole.EXAMCENTER: {
            const role = line.user.roles.find(role => role.name === UserRole.EXAMCENTER);
            if (!role) return html` role but not role wat? `;
            return html`
              <x-flex center>
                <x-text> ${role.contexts.length} Standort${role.contexts.length > 1 ? 'e' : ''} zugewiesen </x-text>
              </x-flex>
            `;
          }
          case UserRole.EXAMCENTERMANAGER: {
            const role = line.user.roles.find(role => role.name === UserRole.EXAMCENTERMANAGER);
            if (!role) return html` role but not role wat? `;
            return html`
              <x-flex center>
                <x-text> ${role.contexts.length} Standort${role.contexts.length > 1 ? 'e' : ''} zugewiesen </x-text>
              </x-flex>
            `;
          }

          case UserRole.INSPECTOR: {
            const role = line.user.roles.find(role => role.name === UserRole.INSPECTOR);
            if (!role) return html` role but not role wat? `;
            return html`
              <x-flex space="xs" center>
                <x-text> ${line.user.specialization} </x-text>
                <x-text> ${role.contexts.join(' ')} </x-text>
              </x-flex>
            `;
          }
          default:
            return html`<x-flex center> </x-flex>`;
        }
      },
      renderHeader: () => nothing,
    };
  }

  private _ordersColumn(): ColumnDefinition<typeof ILSUserOverviewLine> | null {
    if (this.selectedRole?.value !== UserRole.TRAINEE || !hasPermission(this._currentUser.value, 'orders', 'view'))
      return null;
    return {
      columnName: 'Order-Status',
      customID: '_orderStatus',
      selectionSet: {
        ordersOnExamDays: {
          examDayID: 1,
          paymentIntent: 1,
        },
      },
      renderItem: (line: ILSUserOverviewLine) => html`
        <x-flex center crossaxis-end>
          <ils-admin-order-detail-trigger
            .user=${line.user}
            .examDays=${line.ordersOnExamDays}
            @update-required=${() => this._updateLine(line)}
          ></ils-admin-order-detail-trigger>
        </x-flex>
      `,
      renderHeader: () => html` <x-h4 align="right" ${css({ width: '100%' })}> Order-Status </x-h4> `,
    };
  }

  private _nameColumn(): ColumnDefinition<typeof ILSUserOverviewLine> {
    return {
      customID: 'fullName',
      columnName: 'Name',
      selectionSet: { user: { ecommerce: { firstName: 1, lastName: 1 }, email: 1 } },
      renderItem: (line: ILSUserOverviewLine) => {
        return html`
          <x-flex horizontal crossaxis-center space="sm">
            <x-flex space="xs">
              <x-flex ${css({ position: 'relative' })}>
                <x-text bold> ${line.user.ecommerce?.fullName} </x-text> ${this._onlineUsers.value?.includes(
                  line.user._id,
                )
                  ? onlineStatusDot(this)
                  : nothing}
              </x-flex>
              <x-button mode="text" icon="mail" href="mailto:${line.user.email}"> ${line.user.email} </x-button>
            </x-flex>
            ${line.user._id === this._currentUser.value?._id
              ? html`
                  <x-flex>
                    <x-icon ${css({ color: this.colors.accent })}> account_circle </x-icon>
                    <x-tooltip> <x-text> Aktuell eingeloggt </x-text> </x-tooltip>
                  </x-flex>
                `
              : nothing}
          </x-flex>
        `;
      },
    };
  }

  private _actionsColumn(): ColumnDefinition<typeof ILSUserOverviewLine> {
    return {
      columnName: '',
      columnWidth: 'min-content',
      customID: '_edit',
      selectionSet: { user: { _id: 1 } },
      renderItem: (line: ILSUserOverviewLine) => html`
        <x-flex horizontal center>
          ${permissionGuard({
            resource: 'users',
            action: 'modify',
            data: line.user,
            guardedTemplate: html`
              <x-button
                mode="icon"
                icon="edit"
                @click=${() => ILSUserEdit.showPopup({ props: { userID: line.user._id } })}
              >
                <x-tooltip> <x-text> Nutzer bearbeiten </x-text> </x-tooltip>
              </x-button>
            `,
          })}
          ${permissionGuard({
            resource: 'users',
            action: 'view',
            data: line.user,
            guardedTemplate: html`
              <x-button mode="icon" icon="visibility" @click=${() => openUserDetailDialog(line.user._id)}>
                <x-tooltip> <x-text> Nutzerdetails ansehen </x-text> </x-tooltip>
              </x-button>
            `,
          })}
          ${line.user._id !== this._currentUser.value?._id &&
          hasPermission(this._currentUser.value, 'users', 'impersonate')
            ? html`
                <x-button
                  mode="icon"
                  icon="person_pin"
                  mode="outline"
                  @click=${async () => {
                    await impersonateUser(line.user._id, Names.AdornisUser._class)();
                    goTo('/start');
                    window.location.reload();
                  }}
                >
                  <x-tooltip> <x-text> Impersonate User </x-text> </x-tooltip>
                </x-button>
              `
            : nothing}
          ${permissionGuard({
            resource: 'users',
            action: 'modify',
            data: line.user,
            guardedTemplate: html`
              <x-button icon="vpn_key" mode="icon" tone="error" @click=${() => openPasswordChangeDialog(line.user._id)}>
                <x-tooltip> <x-text> Passwort ändern </x-text> </x-tooltip>
              </x-button>
            `,
          })}
          ${permissionGuard({
            resource: 'users',
            action: 'delete',
            data: line.user,
            guardedTemplate: html`
              <x-button
                icon="delete"
                mode="icon"
                tone="error"
                @click=${async () => {
                  if (
                    await XNativeDialog.confirm(
                      'Nutzer wirklich UNWIDERRUFLICH löschen? Alle dem Nutzer zugehörigen Daten werden entsprechend auch gelöscht!',
                    )
                  ) {
                    try {
                      // TODO: archive users
                      await line.user.remove();
                      void XSnackbar.show('Nutzer wurde gelöscht');
                    } catch (e) {
                      void XSnackbar.show('User konnte nicht gelöscht werden');
                    }
                  }
                }}
              >
                <x-tooltip> <x-text> Nutzer entfernen </x-text> </x-tooltip>
              </x-button>
            `,
          })}
        </x-flex>
      `,
    };
  }

  private _downloadSignups() {
    return XNativeDialog.showPopup<void>({
      content: resolve => {
        let month: number = DateTime.now().month - 1;
        let year = DateTime.now().year;
        return html`
          <x-flex space="md" padding="md">
            <x-h4> Anmeldungen herunterladen für den Monat... </x-h4>
            <x-flex horizontal crossaxis-end space="sm">
              <x-input
                type="number"
                placeholder="MM"
                placeholder-mode="inline-floating"
                .value=${month}
                @value-picked=${(e: ValueEvent<number>) => (month = e.detail.value)}
              ></x-input>
              <x-input
                type="number"
                placeholder="JJJJ"
                placeholder-mode="inline-floating"
                .value=${year}
                @value-picked=${(e: ValueEvent<number>) => (year = e.detail.value)}
              ></x-input>
            </x-flex>
            <x-flex horizontal end space="sm">
              <x-button mode="outline" @click=${() => resolve()}> Abbrechen </x-button>
              <x-button
                @click=${() => {
                  if (month < 1 || month > 12) return XNativeDialog.alert('Monat muss zwischen 01 und 12 liegen');
                  if (year < 2000 || year > DateTime.now().year) return XNativeDialog.alert('Jahr ist ungültig');
                  return XNativeDialog.waitFor(
                    downloadLink(
                      A.absoluteUrl(
                        '/api/csv/signups',
                        new URLSearchParams({ month: month.toString(), year: year.toString() }),
                      ),
                      `Anmeldungen_${month}_${year}.csv`,
                    ),
                    'Generiere CSV Datei...',
                  );
                }}
              >
                Herunterladen
              </x-button>
            </x-flex>
          </x-flex>
        `;
      },
    });
  }

  private async _updateLine(line: ILSUserOverviewLine) {
    await getUserOverviewLine(line.user._id)({
      user: {
        _id: 1,
        assignedOSCETypes: 1,
        ecommerce: { firstName: 1, lastName: 1 },
        specialization: 1,
        roles: selectionSet(() => AdornisRoleToContextsWrapper, 2),
        email: 1,
        username: 1,
      },
      ordersOnExamDays: {
        examDayID: 1,
        paymentIntent: 1,
      },
      candidateOfExamDays: {
        examDayID: 1,
        name: 1,
      },
    });
    this.requestUpdate();
  }
}

const changePasswordDialog = stateful(({ userID, resolve }: { userID: string; resolve: () => void }) => {
  const [newPassword, setNewPassword] = useState('');
  return html`
    <x-flex space="md" padding="lg">
      <x-h3> Passwort ändern </x-h3>
      <x-input
        id="newPassword"
        placeholder="Neues Passwort"
        type="password"
        @value-changed=${(e: ValueEvent<string>) => setNewPassword(e.detail.value)}
      ></x-input>
      <x-button
        @click=${async () => {
          try {
            await changePassword(userID, newPassword);
            resolve();
            void XSnackbar.show('Password erfolgreich gesetzt.');
          } catch (error: any) {
            void XNativeDialog.alert(error?.message || error || '');
          }
        }}
      >
        Speichern
      </x-button>
    </x-flex>
  `;
});

const openPasswordChangeDialog = (userID: string) =>
  XNativeDialog.showPopup<void>({ content: resolve => changePasswordDialog({ userID, resolve }) });

const openUserDetailDialog = (userID: string) =>
  XNativeDialog.showPopup<void>({
    content: (close, cancel) => ILSUserView({ userID, close, cancel }),
  });
